import { useMemo } from "react"
import { DateTime } from "luxon"
import { Invoice, PaymentOrderState } from "@oysterhr/schema"
import { type PaymentHistoryItem } from "../types.d"
import { PaymentHistoryItemType } from "../constants"
import { useInvoiceCharges } from "./useInvoiceCharges"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"

interface Props {
  invoice: Invoice
}

export const useInvoicePaymentHistory = ({ invoice }: Props): PaymentHistoryItem[] => {
  const { isStaff, isCompany } = useAccountAspect()
  const canViewPaymentHistory = isStaff || isCompany
  const { data } = useInvoiceCharges({
    skip: !canViewPaymentHistory || !invoice.chargePaymentRequest.id,
    variables: {
      paymentRequestId: invoice.chargePaymentRequest.id || "",
    },
  })

  return useMemo(() => {
    const charges =
      data?.chargePaymentRequest?.orders
        ?.filter(charge => charge.state === PaymentOrderState.Paid)
        ?.map<PaymentHistoryItem>(charge => ({
          type: PaymentHistoryItemType.Charge,
          date: DateTime.fromISO(charge.completedAt || charge.createdAt),
          amount: charge.amount,
          reference: charge.provider === "Stripe" ? "Direct Debit" : "Wire",
        })) || []

    const history =
      invoice.creditNoteApplications?.map(({ appliedCreditNote, amountUsed, createdAt }) => ({
        type: PaymentHistoryItemType.CreditNote,
        date: DateTime.fromISO(createdAt),
        amount: amountUsed,
        reference: appliedCreditNote.referenceCode,
      })) || []

    return [...history, ...charges].sort((a, b) => b.date.toMillis() - a.date.toMillis())
  }, [data?.chargePaymentRequest?.orders, invoice])
}
